/**
 * DemoWritePrompt
 *
 * Shows when a demo user tries a write action (create, sync, save, delete).
 * The backend returns { code: 'DEMO_READ_ONLY' } on write attempts.
 * This component intercepts that and shows a friendly upgrade prompt.
 *
 * Usage in any page:
 *   import { useDemoPrompt } from '../components/DemoWritePrompt';
 *
 *   const { checkDemo, DemoModal } = useDemoPrompt();
 *
 *   async function handleSave() {
 *     try {
 *       await api.post('/jobs', data);
 *     } catch (err) {
 *       if (checkDemo(err)) return;
 *       toast.error(getErrorMessage(err));
 *     }
 *   }
 *
 *   return <div>...<DemoModal /></div>;
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

export function useDemoPrompt() {
  const [show, setShow] = useState(false);
  const { company } = useAuth();

  function checkDemo(err) {
    const isDemoError = err?.response?.data?.code === 'DEMO_READ_ONLY'
      || (company?.isDemo && err?.response?.status === 403);
    if (isDemoError) { setShow(true); return true; }
    return false;
  }

  const DemoModal = () => show ? <DemoWritePrompt onClose={() => setShow(false)} /> : null;

  return { checkDemo, DemoModal };
}

export default function DemoWritePrompt({ onClose }) {
  const navigate = useNavigate();

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 36,
        maxWidth: 440,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚡</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 10px', letterSpacing: '-0.3px' }}>
          Ready to use Jobhub for real?
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 8px' }}>
          You're in the demo — your changes aren't saved. Subscribe to connect your own QuickBooks and payroll data.
        </p>
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 10, padding: '12px 16px', margin: '16px 0',
          fontSize: 13, color: '#166534',
          display: 'flex', flexDirection: 'column', gap: 5,
          textAlign: 'left',
        }}>
          {[
            'Connect your real QuickBooks in 2 minutes',
            'All features, all payroll systems, all reports',
            'Flat-rate pricing — no per-seat fees',
            'Cancel anytime',
          ].map(f => (
            <div key={f} style={{ display: 'flex', gap: 8 }}>
              <span>✓</span><span>{f}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '11px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#374151' }}
          >
            Keep exploring
          </button>
          <button
            onClick={() => { onClose(); navigate('/billing'); }}
            style={{ flex: 2, padding: '11px', background: '#1a2535', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Get started →
          </button>
        </div>
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 14 }}>
          Already have an account? <a href="/login" style={{ color: '#1d4ed8', textDecoration: 'none' }}>Sign in</a>
        </p>
      </div>
    </div>
  );
}